// The live Claude model list, fetched once per session.
//
// Lifted out of AIPromptsEditor (which owned it privately) so the single-ref
// enrichment dialog's per-run model picker reads the same list through the same
// session cache — one fetch serves both screens.

import { useEffect, useState } from 'react';
import { listAnthropicModels } from '../services/firebase';
import {
  curateModelChoices,
  type ModelChoiceInput,
} from '../services/anthropicModelChoices';

/** One entry in a model dropdown. `title` is hover text, not the label. */
export interface ModelOption {
  id: string;
  title: string;
}

/**
 * Last-resort list, used ONLY when the `listAnthropicModels` callable fails
 * (Anthropic unreachable, secret unset, non-admin). Deliberately not
 * maintained: it exists to keep the screens usable, not to be current.
 */
export const FALLBACK_MODELS = [
  'claude-sonnet-4-6',
  'claude-opus-4-6',
  'claude-opus-4-7',
  'claude-haiku-4-5-20251001',
] as const;

/**
 * Session-scoped cache shared across every mount (module scope, so it dies with
 * the page — a reload always re-fetches). `modelListRequest` de-dupes concurrent
 * mounts and is cleared on failure so a later mount retries rather than
 * replaying the rejection forever.
 */
let modelListCache: ModelChoiceInput[] | null = null;
let modelListRequest: Promise<ModelChoiceInput[]> | null = null;

/** Reset the session cache. Tests only. */
export function __resetAnthropicModelCache(): void {
  modelListCache = null;
  modelListRequest = null;
}

export interface UseAnthropicModelsOptions {
  /**
   * True for the per-run picker: only the newest release of each frontier
   * family (see curateModelChoices). False — the default — is every model
   * Anthropic lists, which is what the AI Prompts editor needs to pin an older
   * id for a prompt type.
   */
  curated?: boolean;
}

export function useAnthropicModels(options: UseAnthropicModelsOptions = {}): {
  options: ModelOption[];
  loading: boolean;
  failed: boolean;
} {
  const { curated = false } = options;
  const [fetched, setFetched] = useState<ModelChoiceInput[] | null>(modelListCache);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (modelListCache) return;
    let cancelled = false;

    if (!modelListRequest) {
      modelListRequest = listAnthropicModels()
        .then(({ models }) => {
          if (models.length === 0) {
            // An empty list is a bug upstream, not a valid answer — treat it as
            // a failure so we fall back instead of rendering an empty dropdown.
            throw new Error('listAnthropicModels returned no models');
          }
          modelListCache = models;
          return models;
        })
        .catch((err) => {
          modelListRequest = null;
          throw err;
        });
    }

    modelListRequest.then(
      (models) => {
        if (!cancelled) setFetched(models);
      },
      (err) => {
        console.error(
          'listAnthropicModels failed — falling back to the local model list:',
          err,
        );
        if (!cancelled) setFailed(true);
      },
    );

    return () => {
      cancelled = true;
    };
  }, []);

  if (fetched) {
    const list = curated
      ? curateModelChoices(fetched).map((c) => ({ id: c.id, title: c.displayName }))
      : fetched.map((m) => ({ id: m.id, title: m.displayName }));
    // A curated filter that matched nothing (an unfamiliar naming scheme, say)
    // must not leave an empty picker — show everything rather than nothing.
    return {
      options: list.length > 0 ? list : fetched.map((m) => ({ id: m.id, title: m.displayName })),
      loading: false,
      failed: false,
    };
  }
  if (failed) {
    const fallback = FALLBACK_MODELS.map((id) => ({ id, displayName: 'Local fallback list' }));
    const list = curated ? curateModelChoices(fallback) : fallback.map((m) => ({ ...m }));
    return {
      options: list.map((m) => ({ id: m.id, title: 'Local fallback list' })),
      loading: false,
      failed: true,
    };
  }
  return { options: [], loading: true, failed: false };
}
